import { apiFetch, apiFetchBlob, apiFetchText } from "./api";
import type { PagedResult } from "./clients-accounts";

// ============================================================================
// DTOs (mirror src/Allmark.Contracts/InvoicesPayments/*.cs)
//
// Wire-format casing: ASP.NET Core System.Text.Json default = camelCase.
// Type adaptations:
//   - byte[] (RowVersion) → string (System.Text.Json serialises byte[] as base64).
//   - decimal → number.
//   - DateTime / DateTime? → string (ISO 8601 from JSON).
//   - IReadOnlyList<T> → T[].
// ============================================================================

// ----------------------------------------------------------------------------
// Invoice
// ----------------------------------------------------------------------------

export interface InvoiceDto {
  invoiceId: number;
  invoiceReferenceNo: string;
  invoiceDate: string | null;
  dueDate: string | null;
  jobId: number | null;
  accountId: number | null;
  clientId: number | null;
  companyId: number | null;
  addressId: number | null;
  issuedAtUtc: string | null;
  invoiceStatusCode: string;
  invoiceTotalIncGst: number;
  invoiceTotalExGst: number;
  /** Maps to C# Gst → camelCase wire: gst */
  gst: number;
  amountPaid: number;
  balanceOutstanding: number;
  deliveryChargeExGst: number;
  commentToShowOnInvoice: string | null;
  pymtReferenceNo: string | null;
  pdfBlobPath: string | null;
  cancelledAtUtc: string | null;
  cancelReason: string | null;
  myobExportedAtUtc: string | null;
  reminderIssuedOn: string | null;
  paidOn: string | null;
  /** byte[] RowVersion serialised as base64 string. */
  rowVersion: string;
}

export interface InvoiceSummaryDto {
  invoiceId: number;
  invoiceReferenceNo: string;
  invoiceDate: string | null;
  dueDate: string | null;
  accountId: number | null;
  accountReferenceNo: string | null;
  clientId: number | null;
  clientFullName: string | null;
  invoiceTotalIncGst: number;
  balanceOutstanding: number;
  invoiceStatusCode: string;
  myobExported: boolean;
}

export interface CreateInvoiceRequest {
  clientId: number;
  accountId: number | null;
  jobId: number | null;
  addressId: number | null;
  deliveryChargeExGst: number | null;
  lines: CreateInvoiceLineItemRequest[];
  commentToShowOnInvoice: string | null;
  dueDate: string | null;
}

export interface UpdateInvoiceRequest {
  deliveryChargeExGst: number | null;
  commentToShowOnInvoice: string | null;
  dueDate: string | null;
  /** Base64-encoded RowVersion echoed from the previous read for concurrency. */
  rowVersion: string;
}

export interface IssueInvoiceRequest {
  rowVersion: string;
}

export interface CancelInvoiceRequest {
  reason: string;
  rowVersion: string;
}

export interface SendReminderRequest {
  customMessage: string | null;
}

// ----------------------------------------------------------------------------
// InvoiceLineItem
// ----------------------------------------------------------------------------

export interface InvoiceLineItemDto {
  lineItemId: number;
  invoiceId: number;
  jobDetailId: number | null;
  description: string;
  quantity: number;
  unitPriceExGst: number;
  discountPct: number;
  totalExGst: number;
  gstApplicable: boolean;
  isCredit: boolean;
  sortOrder: number | null;
}

export interface CreateInvoiceLineItemRequest {
  description: string;
  quantity: number;
  unitPriceExGst: number;
  discountPct: number;
  gstApplicable: boolean;
  isCredit: boolean;
  jobDetailId: number | null;
  sortOrder: number | null;
}

export interface UpdateInvoiceLineItemRequest {
  description: string;
  quantity: number;
  unitPriceExGst: number;
  discountPct: number;
  gstApplicable: boolean;
  isCredit: boolean;
  sortOrder: number | null;
}

// ----------------------------------------------------------------------------
// InvoiceTransactionMapping
// ----------------------------------------------------------------------------

export interface InvoiceTransactionMappingDto {
  invoiceTransactionMapId: number;
  invoiceId: number;
  tranId: number | null;
  paymentRecordId: number | null;
  amount: number;
  note: string | null;
  createdOn: string | null;
  createdByName: string | null;
}

// ----------------------------------------------------------------------------
// External / public token-based view
// ----------------------------------------------------------------------------

export interface ExternalInvoiceDto {
  invoiceReferenceNo: string;
  invoiceDate: string | null;
  dueDate: string | null;
  invoiceTotalIncGst: number;
  balanceOutstanding: number;
  downloadPdfUrl: string;
}

// ----------------------------------------------------------------------------
// InvoiceStatus (admin lookup)
// ----------------------------------------------------------------------------

export interface InvoiceStatusDto {
  invoiceStatusCode: string;
  description: string;
  sortOrder: number | null;
}

export interface CreateInvoiceStatusRequest {
  invoiceStatusCode: string;
  description: string;
  sortOrder: number | null;
}

export interface UpdateInvoiceStatusRequest {
  description: string;
  sortOrder: number | null;
}

// ----------------------------------------------------------------------------
// TransactionType (admin lookup)
// ----------------------------------------------------------------------------

export interface TransactionTypeDto {
  tranTypeCode: string;
  description: string;
  isCredit: boolean;
  sortOrder: number | null;
}

export interface CreateTransactionTypeRequest {
  tranTypeCode: string;
  description: string;
  isCredit: boolean;
  sortOrder: number | null;
}

export interface UpdateTransactionTypeRequest {
  description: string;
  isCredit: boolean;
  sortOrder: number | null;
}

// ----------------------------------------------------------------------------
// List query params (mirrors backend `ListInvoicesQuery` shape)
// ----------------------------------------------------------------------------

export interface ListInvoicesParams {
  status?: string | string[];
  clientId?: number;
  accountId?: number;
  /** ISO date or date-time (inclusive lower bound on IssuedAtUtc). */
  from?: string;
  /** ISO date or date-time (exclusive upper bound on IssuedAtUtc). */
  to?: string;
  dueFrom?: string;
  dueTo?: string;
  minTotal?: number;
  maxTotal?: number;
  q?: string;
  page?: number;
  pageSize?: number;
}

// ============================================================================
// API helpers
// ============================================================================

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        sp.append(k, String(item));
      }
    } else {
      sp.append(k, String(v));
    }
  }
  return sp.toString();
}

// ---- Invoices ---------------------------------------------------------------

export const listInvoices = (q: ListInvoicesParams = {}): Promise<PagedResult<InvoiceSummaryDto>> => {
  const search = qs(q as Record<string, unknown>);
  return apiFetch<PagedResult<InvoiceSummaryDto>>(`/api/invoices${search ? `?${search}` : ""}`);
};

export const getInvoice = (id: number): Promise<InvoiceDto> =>
  apiFetch<InvoiceDto>(`/api/invoices/${id}`);

export const createInvoice = (body: CreateInvoiceRequest): Promise<InvoiceDto> =>
  apiFetch<InvoiceDto>("/api/invoices", { method: "POST", body: JSON.stringify(body) });

export const updateInvoice = (id: number, body: UpdateInvoiceRequest): Promise<InvoiceDto> =>
  apiFetch<InvoiceDto>(`/api/invoices/${id}`, { method: "PATCH", body: JSON.stringify(body) });

export const deleteInvoice = (id: number): Promise<void> =>
  apiFetch<void>(`/api/invoices/${id}`, { method: "DELETE" });

// ---- Invoice line items -----------------------------------------------------

export const listInvoiceLineItems = (invoiceId: number): Promise<InvoiceLineItemDto[]> =>
  apiFetch<InvoiceLineItemDto[]>(`/api/invoices/${invoiceId}/line-items`);

export const createInvoiceLineItem = (
  invoiceId: number,
  body: CreateInvoiceLineItemRequest,
): Promise<InvoiceLineItemDto> =>
  apiFetch<InvoiceLineItemDto>(`/api/invoices/${invoiceId}/line-items`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updateInvoiceLineItem = (
  invoiceId: number,
  lineId: number,
  body: UpdateInvoiceLineItemRequest,
): Promise<InvoiceLineItemDto> =>
  apiFetch<InvoiceLineItemDto>(`/api/invoices/${invoiceId}/line-items/${lineId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const deleteInvoiceLineItem = (invoiceId: number, lineId: number): Promise<void> =>
  apiFetch<void>(`/api/invoices/${invoiceId}/line-items/${lineId}`, { method: "DELETE" });

// ---- Invoice orchestrators --------------------------------------------------

export const issueInvoice = (id: number, body: IssueInvoiceRequest): Promise<InvoiceDto> =>
  apiFetch<InvoiceDto>(`/api/invoices/${id}/issue`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const cancelInvoice = (id: number, body: CancelInvoiceRequest): Promise<InvoiceDto> =>
  apiFetch<InvoiceDto>(`/api/invoices/${id}/cancel`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const sendInvoiceReminder = (id: number, body: SendReminderRequest): Promise<void> =>
  apiFetch<void>(`/api/invoices/${id}/send-reminder`, {
    method: "POST",
    body: JSON.stringify(body),
  });

// ---- Invoice transactions / pdf ---------------------------------------------

export const getInvoiceTransactions = (id: number): Promise<InvoiceTransactionMappingDto[]> =>
  apiFetch<InvoiceTransactionMappingDto[]>(`/api/invoices/${id}/transactions`);

/** Returns a URL string suitable for direct anchor href / iframe src. Does not fetch. */
export const getInvoicePdfUrl = (id: number): string => `/api/invoices/${id}/pdf`;

// ---- Delivery parcel labels --------------------------------------------------

/**
 * One 100x150mm parcel label per parcel, as a PDF — the same artefact the job screen prints.
 *
 * Reached from the invoice screen for the many deliveries that never had a job: the server
 * resolves the delivery party from the linked job when there is one and from the invoice's own
 * client when there is not.
 */
export const printInvoiceDeliverySticker = (id: number, parcelCount: number | null): Promise<Blob> =>
  apiFetchBlob(`/api/invoices/${id}/delivery-sticker/print`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ parcelCount }),
  });

/**
 * The same labels as native ZPL, for the workstation-agent path — the bytes go straight to a
 * label printer, with no PDF and no browser print dialog. PDF stays the default and the fallback
 * when no agent is present.
 *
 * Deliberately has no `disposition` parameter, unlike the job route: that one exists only to keep
 * a diagnostic download out of the job's event feed, and this endpoint writes no event at all
 * (a job-less invoice has nothing to attach one to). A disposition here would be a query string
 * the server never reads.
 */
export const printInvoiceDeliveryStickerZpl = (id: number, parcelCount: number | null): Promise<string> =>
  apiFetchText(`/api/invoices/${id}/delivery-sticker/print?format=zpl`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ parcelCount }),
  });

// ---- External (anonymous, token-based) --------------------------------------

export const getExternalInvoice = (token: string): Promise<ExternalInvoiceDto> =>
  apiFetch<ExternalInvoiceDto>(`/api/external/invoices/${encodeURIComponent(token)}`);

/** Returns a URL string for the public PDF endpoint. Does not fetch. */
export const getExternalInvoicePdfUrl = (token: string): string =>
  `/api/external/invoices/${encodeURIComponent(token)}/pdf`;

// ---- Transaction types (admin lookup) ---------------------------------------

export const listTransactionTypes = (): Promise<TransactionTypeDto[]> =>
  apiFetch<TransactionTypeDto[]>("/api/admin/transaction-types");

export const getTransactionType = (code: string): Promise<TransactionTypeDto> =>
  apiFetch<TransactionTypeDto>(`/api/admin/transaction-types/${encodeURIComponent(code)}`);

export const createTransactionType = (body: CreateTransactionTypeRequest): Promise<TransactionTypeDto> =>
  apiFetch<TransactionTypeDto>("/api/admin/transaction-types", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updateTransactionType = (
  code: string,
  body: UpdateTransactionTypeRequest,
): Promise<TransactionTypeDto> =>
  apiFetch<TransactionTypeDto>(`/api/admin/transaction-types/${encodeURIComponent(code)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const deleteTransactionType = (code: string): Promise<void> =>
  apiFetch<void>(`/api/admin/transaction-types/${encodeURIComponent(code)}`, { method: "DELETE" });

// ---- Invoice statuses (admin lookup) ----------------------------------------

export const listInvoiceStatuses = (): Promise<InvoiceStatusDto[]> =>
  apiFetch<InvoiceStatusDto[]>("/api/admin/invoice-statuses");

export const getInvoiceStatus = (code: string): Promise<InvoiceStatusDto> =>
  apiFetch<InvoiceStatusDto>(`/api/admin/invoice-statuses/${encodeURIComponent(code)}`);

export const createInvoiceStatus = (body: CreateInvoiceStatusRequest): Promise<InvoiceStatusDto> =>
  apiFetch<InvoiceStatusDto>("/api/admin/invoice-statuses", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updateInvoiceStatus = (
  code: string,
  body: UpdateInvoiceStatusRequest,
): Promise<InvoiceStatusDto> =>
  apiFetch<InvoiceStatusDto>(`/api/admin/invoice-statuses/${encodeURIComponent(code)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const deleteInvoiceStatus = (code: string): Promise<void> =>
  apiFetch<void>(`/api/admin/invoice-statuses/${encodeURIComponent(code)}`, { method: "DELETE" });

// ============================================================================
// Plan II additions
// ============================================================================

// ----------------------------------------------------------------------------
// PaymentRecord
// ----------------------------------------------------------------------------

export interface PaymentAllocationDto {
  invoiceId: number;
  amount: number;
}

export interface PaymentRecordDto {
  paymentRecordId: number;
  invoiceId: number | null;
  accountId: number | null;
  tranAmount: number;
  transactionDate: string;
  paymentTypeCode: string;
  paymentMethodCode: string | null;
  paymentGatewayCode: string | null;
  pymtReferenceNo: string | null;
  status: string;
  processedAtUtc: string | null;
  reversedAtUtc: string | null;
  reversalReason: string | null;
  notes: string | null;
  createdOn: string | null;
  createdByName: string | null;
  /** byte[] RowVersion serialised as base64 string by System.Text.Json. */
  rowVersion: string | null;
  /** Last-edit audit (PATCH /api/payments/{id}); null if never edited. */
  editedAtUtc: string | null;
  /** Last-edit audit (PATCH /api/payments/{id}); null if never edited. */
  editedByName: string | null;
  /** Allocations attached to this payment; null if not yet loaded. */
  allocations: PaymentAllocationDto[] | null;
}

export interface PaymentRecordSummaryDto {
  paymentRecordId: number;
  invoiceId: number | null;
  accountId: number | null;
  tranAmount: number;
  transactionDate: string;
  paymentTypeCode: string;
  status: string;
  allocationCount: number;
}

export interface PaymentAllocationRequest {
  invoiceId: number;
  amount: number;
}

export interface CreatePaymentRequest {
  invoiceId: number | null;
  accountId: number | null;
  amount: number;
  transactionDate: string;
  paymentTypeCode: string;
  paymentMethodCode: string | null;
  paymentGatewayCode: string | null;
  pymtReferenceNo: string | null;
  notes: string | null;
  idempotencyKey: string | null;
  allocations: PaymentAllocationRequest[] | null;
}

export interface ReversePaymentRequest {
  reason: string;
  /** Base64-encoded RowVersion echoed from the previous read. */
  rowVersion: string | null;
}

export interface UpdatePaymentRequest {
  amount: number;
  transactionDate: string;
  paymentTypeCode: string;
  paymentMethodCode: string | null;
  paymentGatewayCode: string | null;
  pymtReferenceNo: string | null;
  notes: string | null;
  editReason: string;
  allocations: PaymentAllocationRequest[] | null;
  /** Base64-encoded RowVersion echoed from the previous read. */
  rowVersion: string | null;
}

// ----------------------------------------------------------------------------
// PaymentMethod (admin lookup)
// ----------------------------------------------------------------------------

export interface PaymentMethodDto {
  id: number;
  description: string;
  pymtCode: string | null;
  clientInfoMsg: string | null;
  displayOnClientSite: boolean;
  acceptedOnInvoicePdf: boolean;
  autoOnly: boolean;
  autoReconcile: boolean;
  sortOrder: number;
}

export interface CreatePaymentMethodRequest {
  description: string;
  pymtCode: string | null;
  clientInfoMsg: string | null;
  displayOnClientSite: boolean;
  acceptedOnInvoicePdf: boolean;
  autoOnly: boolean;
  autoReconcile: boolean;
  sortOrder: number;
}

export interface UpdatePaymentMethodRequest {
  description: string;
  pymtCode: string | null;
  clientInfoMsg: string | null;
  displayOnClientSite: boolean;
  acceptedOnInvoicePdf: boolean;
  autoOnly: boolean;
  autoReconcile: boolean;
  sortOrder: number;
}

// ----------------------------------------------------------------------------
// PaymentType (admin lookup)
// ----------------------------------------------------------------------------

export interface PaymentTypeDto {
  paymentTypeCode: string;
  description: string;
  active: boolean;
}

export interface CreatePaymentTypeRequest {
  paymentTypeCode: string;
  description: string;
  active: boolean;
}

export interface UpdatePaymentTypeRequest {
  description: string;
  active: boolean;
}

// ----------------------------------------------------------------------------
// PaymentGateway (admin lookup)
// ----------------------------------------------------------------------------

export interface PaymentGatewayDto {
  paymentGatewayCode: string;
  description: string;
  /** Maps to C# AccountId (string, not integer). */
  accountId: string | null;
  active: boolean;
}

export interface CreatePaymentGatewayRequest {
  paymentGatewayCode: string;
  description: string;
  accountId: string | null;
  active: boolean;
}

export interface UpdatePaymentGatewayRequest {
  description: string;
  accountId: string | null;
  active: boolean;
}

// ----------------------------------------------------------------------------
// Reconciliation / Transactions
// ----------------------------------------------------------------------------

export interface TransactionRowDto {
  tranId: number;
  transactionDate: string;
  accountId: number;
  accountReferenceNo: string;
  tranTypeCode: string;
  invoiceId: number | null;
  invoiceReferenceNo: string | null;
  paymentRecordId: number | null;
  paymentReferenceNo: string | null;
  tranAmount: number;
  runningBalance: number;
  createdByName: string | null;
}

export interface ReconciliationTodayDto {
  paymentsCount: number;
  paymentsTotal: number;
  invoicesIssuedCount: number;
  invoicesIssuedTotal: number;
  transactionsCount: number;
  failedPaymentPostingsCount: number;
  recentPayments: PaymentRecordSummaryDto[];
  recentInvoicesIssued: InvoiceSummaryDto[];
  recentTransactions: TransactionRowDto[];
}

// ----------------------------------------------------------------------------
// List params for transactions
// ----------------------------------------------------------------------------

export interface ListTransactionsParams {
  accountId?: number;
  typeCode?: string;
  from?: string;
  to?: string;
  invoiceId?: number;
  paymentRecordId?: number;
  minAmount?: number;
  maxAmount?: number;
  page?: number;
  pageSize?: number;
}

// ---- Payments ---------------------------------------------------------------

export const listPayments = (params: {
  status?: string[];
  accountId?: number;
  invoiceId?: number;
  paymentTypeCode?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<PagedResult<PaymentRecordSummaryDto>> => {
  const sp = new URLSearchParams();
  if (params.status?.length) params.status.forEach((s) => sp.append("status", s));
  if (params.accountId != null)     sp.set("accountId", String(params.accountId));
  if (params.invoiceId != null)     sp.set("invoiceId", String(params.invoiceId));
  if (params.paymentTypeCode)       sp.set("paymentTypeCode", params.paymentTypeCode);
  if (params.from)                  sp.set("from", params.from);
  if (params.to)                    sp.set("to", params.to);
  if (params.page != null)          sp.set("page", String(params.page));
  if (params.pageSize != null)      sp.set("pageSize", String(params.pageSize));
  const search = sp.toString();
  return apiFetch<PagedResult<PaymentRecordSummaryDto>>(`/api/payments${search ? `?${search}` : ""}`);
};

export const getPayment = (id: number): Promise<PaymentRecordDto> =>
  apiFetch<PaymentRecordDto>(`/api/payments/${id}`);

export const createPayment = (body: CreatePaymentRequest): Promise<{ paymentRecordId: number }> =>
  apiFetch<{ paymentRecordId: number }>("/api/payments", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const reversePayment = (id: number, body: ReversePaymentRequest): Promise<void> =>
  apiFetch<void>(`/api/payments/${id}/reverse`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updatePayment = (paymentRecordId: number, body: UpdatePaymentRequest): Promise<void> =>
  apiFetch<void>(`/api/payments/${paymentRecordId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

// ---- PaymentMethod (admin lookup) ------------------------------------------

export const listPaymentMethods = (): Promise<PaymentMethodDto[]> =>
  apiFetch<PaymentMethodDto[]>("/api/admin/payment-methods");

export const getPaymentMethod = (id: number): Promise<PaymentMethodDto> =>
  apiFetch<PaymentMethodDto>(`/api/admin/payment-methods/${id}`);

export const createPaymentMethod = (body: CreatePaymentMethodRequest): Promise<PaymentMethodDto> =>
  apiFetch<PaymentMethodDto>("/api/admin/payment-methods", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updatePaymentMethod = (id: number, body: UpdatePaymentMethodRequest): Promise<PaymentMethodDto> =>
  apiFetch<PaymentMethodDto>(`/api/admin/payment-methods/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const deletePaymentMethod = (id: number): Promise<void> =>
  apiFetch<void>(`/api/admin/payment-methods/${id}`, { method: "DELETE" });

// ---- PaymentType (admin lookup) --------------------------------------------

export const listPaymentTypes = (): Promise<PaymentTypeDto[]> =>
  apiFetch<PaymentTypeDto[]>("/api/admin/payment-types");

export const getPaymentType = (code: string): Promise<PaymentTypeDto> =>
  apiFetch<PaymentTypeDto>(`/api/admin/payment-types/${encodeURIComponent(code)}`);

export const createPaymentType = (body: CreatePaymentTypeRequest): Promise<PaymentTypeDto> =>
  apiFetch<PaymentTypeDto>("/api/admin/payment-types", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updatePaymentType = (code: string, body: UpdatePaymentTypeRequest): Promise<PaymentTypeDto> =>
  apiFetch<PaymentTypeDto>(`/api/admin/payment-types/${encodeURIComponent(code)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const deletePaymentType = (code: string): Promise<void> =>
  apiFetch<void>(`/api/admin/payment-types/${encodeURIComponent(code)}`, { method: "DELETE" });

// ---- PaymentGateway (admin lookup) -----------------------------------------

export const listPaymentGateways = (): Promise<PaymentGatewayDto[]> =>
  apiFetch<PaymentGatewayDto[]>("/api/admin/payment-gateways");

export const getPaymentGateway = (code: string): Promise<PaymentGatewayDto> =>
  apiFetch<PaymentGatewayDto>(`/api/admin/payment-gateways/${encodeURIComponent(code)}`);

export const createPaymentGateway = (body: CreatePaymentGatewayRequest): Promise<PaymentGatewayDto> =>
  apiFetch<PaymentGatewayDto>("/api/admin/payment-gateways", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updatePaymentGateway = (code: string, body: UpdatePaymentGatewayRequest): Promise<PaymentGatewayDto> =>
  apiFetch<PaymentGatewayDto>(`/api/admin/payment-gateways/${encodeURIComponent(code)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const deletePaymentGateway = (code: string): Promise<void> =>
  apiFetch<void>(`/api/admin/payment-gateways/${encodeURIComponent(code)}`, { method: "DELETE" });

// ---- Payment lookup adapters (uniform shape for RecordPaymentModal dropdowns)
//
// PaymentTypeDto, PaymentMethodDto, PaymentGatewayDto have heterogeneous keys
// (paymentTypeCode / id+pymtCode / paymentGatewayCode). Modal/dropdown UI
// wants a single { code, description, active } shape — these adapters provide
// that. Active filtering / sorting is the caller's responsibility.

export interface PaymentLookupItem {
  /** The string the modal stores as paymentTypeCode / paymentMethodCode / paymentGatewayCode. */
  code: string;
  description: string;
  active: boolean;
}

export const listPaymentTypeLookups = async (): Promise<PaymentLookupItem[]> => {
  const rows = await listPaymentTypes();
  return rows.map((r) => ({ code: r.paymentTypeCode, description: r.description, active: r.active }));
};

/**
 * PaymentMethodDto has no Active flag and uses int Id + optional PymtCode.
 * For dropdown purposes we use pymtCode when present, else fall back to id-as-string,
 * and mark every method as active (the admin UI is where soft-deletion happens).
 */
export const listPaymentMethodLookups = async (): Promise<PaymentLookupItem[]> => {
  const rows = await listPaymentMethods();
  return rows.map((r) => ({
    code: r.pymtCode ?? String(r.id),
    description: r.description,
    active: true,
  }));
};

export const listPaymentGatewayLookups = async (): Promise<PaymentLookupItem[]> => {
  const rows = await listPaymentGateways();
  return rows.map((r) => ({ code: r.paymentGatewayCode, description: r.description, active: r.active }));
};

// ---- Reconciliation ---------------------------------------------------------

export const getReconciliationToday = (): Promise<ReconciliationTodayDto> =>
  apiFetch<ReconciliationTodayDto>("/api/reconciliation/today");

// ---- Transactions -----------------------------------------------------------

export const listTransactions = (params: ListTransactionsParams = {}): Promise<PagedResult<TransactionRowDto>> => {
  const search = qs(params as Record<string, unknown>);
  return apiFetch<PagedResult<TransactionRowDto>>(`/api/transactions${search ? `?${search}` : ""}`);
};

// ============================================================================
// Plan III additions — statement runs
// ============================================================================
export * from "./statement-runs";

// ============================================================================
// Jobs Plan I addition — create invoice from job
// ============================================================================

/**
 * Creates an invoice pre-populated from a Job's items by supplying the jobId
 * query parameter. The server derives client, lines, etc. from the job record.
 * Returns the new invoice's id.
 */
export const createInvoiceFromJob = (jobId: number): Promise<{ invoiceId: number }> =>
  apiFetch<{ invoiceId: number }>(`/api/invoices?jobId=${jobId}`, {
    method: "POST",
    body: "{}",
  });
